import { apiFetch } from '../../../../shared/services/apiClient';
import { BolsaDetalle, TipoMarcacion, LineaCotizacion, ColorVariante, MarcacionConfigResponse, SitioEntrega, SitioEntregaSugerida, LineaGuardada, EsquemaMarcacion, OrigenDescuento, OpcionPrecio } from './types';
import { EstadoBolsa } from '../CentroCotizaciones/state/bolsaMachine';

interface ProveedorMarcacion {
  id: number;
  nombre: string;
}

const BASE = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api').replace(/\/api$/, '');

interface RawLinea {
  id: string;
  producto_id: string;
  canonico_id?: number | null;
  nombre_producto?: string;
  imagen_url?: string | null;
  descripcion?: string | null;
  proveedor_id?: number | null;
  nombre_proveedor?: string;
  colores_variantes?: ColorVariante[];
  color_seleccionado?: string | null;
  codigo_color_seleccionado?: string | null;
  tipo_marcacion_id?: number | null;
  marcacion_opcion_id?: number | null;
  marcacion_precio?: number | null;
  marcacion_proveedor_id?: number | null;
  marcacion_esquema?: EsquemaMarcacion | null;
  cantidad: number;
  precio_unitario: number;
  subtotal?: number;
  precio_venta?: number | null;
  lastSynced?: string | null;
  sku?: string | null;
  categoriaNombre?: string | null;
  descuento_proveedor?: number;
  precio_con_descuento?: number | null;
  origen_descuento?: OrigenDescuento | null;
  // SPEC-208 — copia de las opciones de precio e índice elegido; null sin copia.
  opciones_precio?: OpcionPrecio[] | null;
  opcion_precio_indice?: number | null;
  transporte: number;
  // SPEC-195 — solo en GET /bolsas/:id.
  tramos_descuento?: Array<{ monto_desde: number; monto_hasta: number | null; porcentaje: number }>;
}

interface RawSitioEntrega {
  departamentoId: number | null;
  departamento: string | null;
  ciudadId: number | null;
  ciudad: string | null;
}

interface RawBolsa {
  id: string;
  nombre: string;
  total: number;
  lineas_count: number;
  lineas: RawLinea[];
  estado_codigo: EstadoBolsa;
  freight: number;
  codigo_cotizacion?: string | null;
  // SPEC-159 — sitio de entrega persistido + sugerido (del cliente).
  entrega?: RawSitioEntrega | null;
  entregaSugerida?: RawSitioEntrega | null;
  // SPEC-167 — validez persistida (date-only) + sugerencia autoritativa (hoy+15).
  validez?: string | null;
  validez_sugerida?: string | null;
  // SPEC-206 (RN-010) — versión vigente y número del próximo envío.
  version_vigente?: { version: number; enviada_en: string | null } | null;
  siguiente_version?: number;
}

export const ENTREGA_VACIA: SitioEntrega = {
  departamentoId: null,
  departamento: null,
  ciudadId: null,
  ciudad: null,
};

function mapEntrega(raw?: RawSitioEntrega | null): SitioEntrega {
  if (!raw) return { ...ENTREGA_VACIA };
  return {
    departamentoId: raw.departamentoId ?? null,
    departamento: raw.departamento ?? null,
    ciudadId: raw.ciudadId ?? null,
    ciudad: raw.ciudad ?? null,
  };
}

function mapLinea(raw: RawLinea): LineaCotizacion {
  return {
    id: raw.id,
    productoId: raw.producto_id,
    canonicoId: raw.canonico_id ?? null,
    nombre: raw.nombre_producto ?? '',
    imagenUrl: raw.imagen_url ?? null,
    descripcion: raw.descripcion ?? null,
    proveedor: raw.nombre_proveedor ?? '',
    proveedorId: raw.proveedor_id ?? null,
    coloresVariantes: raw.colores_variantes ?? [],
    colorSeleccionado: raw.color_seleccionado ?? null,
    codigoColorSeleccionado: raw.codigo_color_seleccionado ?? null,
    tipoMarcacionId: raw.tipo_marcacion_id ?? null,
    marcacionOpcionId: raw.marcacion_opcion_id ?? null,
    marcacionProveedorId: raw.marcacion_proveedor_id ?? null,
    precioMarcacion: raw.marcacion_precio ?? null,
    marcacionEsquema: raw.marcacion_esquema ?? null,
    cantidad: raw.cantidad,
    precioUnitario: raw.precio_unitario,
    precioVenta: raw.precio_venta ?? null,
    subtotal: raw.subtotal,
    lastSynced: raw.lastSynced ?? null,
    sku: raw.sku ?? null,
    categoriaNombre: raw.categoriaNombre ?? null,
    descuentoProveedor: raw.descuento_proveedor ?? 0,
    precioConDescuento: raw.precio_con_descuento ?? null,
    origenDescuento: raw.origen_descuento ?? null,
    opcionesPrecio: raw.opciones_precio ?? null,
    opcionPrecioIndice: raw.opcion_precio_indice ?? null,
    transporte: raw.transporte,
    tramosDescuento: (raw.tramos_descuento ?? []).map((t) => ({
      montoDesde: t.monto_desde,
      montoHasta: t.monto_hasta,
      porcentaje: t.porcentaje,
    })),
  };
}

export const cotizacionDetalleService = {
  async obtenerBolsa(bolsaId: string): Promise<BolsaDetalle> {
    const res = await apiFetch(`${BASE}/api/cotizaciones/bolsas/${bolsaId}`);
    if (!res.ok) {
      if (res.status === 404) throw new Error('Bolsa no encontrada');
      throw new Error(`Error al cargar la bolsa: ${res.status}`);
    }
    const json = await res.json();
    const raw: RawBolsa = json.data;
    return {
      id: raw.id,
      nombre: raw.nombre,
      total: raw.total,
      lineas_count: raw.lineas_count ?? (raw.lineas?.length ?? 0),
      lineas: (raw.lineas ?? []).map(mapLinea),
      estadoCodigo: raw.estado_codigo,
      freight: raw.freight,
      codigoCotizacion: raw.codigo_cotizacion ?? null,
      entrega: mapEntrega(raw.entrega),
      // SPEC-159 (RN-002) — sugerencia solo si la bolsa tiene cliente (backend envía null si no).
      entregaSugerida: (raw.entregaSugerida ? mapEntrega(raw.entregaSugerida) : null) as SitioEntregaSugerida,
      // SPEC-167 — validez persistida + sugerencia autoritativa (hoy+15) del backend.
      validez: raw.validez ?? null,
      validezSugerida: raw.validez_sugerida ?? null,
      versionVigente: raw.version_vigente
        ? { version: raw.version_vigente.version, enviadaEn: raw.version_vigente.enviada_en ?? null }
        : null,
      siguienteVersion: raw.siguiente_version ?? 1,
    };
  },

  // SPEC-159 — persiste el sitio de entrega (departamento/ciudad). v1.1 (RN-011): sin dirección.
  // Devuelve la entrega guardada (nombres resueltos por el backend). 400 = inconsistente/ubicación
  // inválida, 409 = estado no editable, 404 = bolsa inexistente.
  async actualizarSitioEntrega(
    bolsaId: string,
    payload: { departamentoId: number | null; ciudadId: number | null },
  ): Promise<SitioEntrega> {
    const res = await apiFetch(`${BASE}/api/cotizaciones/bolsas/${bolsaId}/entrega`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      const msg = err?.error?.message ?? 'Error al guardar el sitio de entrega';
      throw new Error(typeof msg === 'string' ? msg : 'Error al guardar el sitio de entrega');
    }
    const json = await res.json();
    return mapEntrega(json.data.entrega as RawSitioEntrega);
  },

  // SPEC-167 (UC-002/003) — persiste la validez de construcción (date-only "YYYY-MM-DD").
  // Devuelve la fecha guardada. 400 = fuera de rango, 409 = estado no editable,
  // 404 = bolsa inexistente.
  async actualizarValidez(bolsaId: string, validez: string): Promise<string | null> {
    const res = await apiFetch(`${BASE}/api/cotizaciones/bolsas/${bolsaId}/validez`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ validez }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      const msg = err?.error?.message ?? 'Error al guardar la fecha de validez';
      throw new Error(typeof msg === 'string' ? msg : 'Error al guardar la fecha de validez');
    }
    const json = await res.json();
    return (json.data?.validez ?? null) as string | null;
  },

  // SPEC-133 (RN-001, UC-001): transiciona ACTIVA → BORRADOR, asigna codigo_cotizacion y
  // genera el PDF inicial (SPEC-126, sin cambios). Refresca el detalle completo tras convertir
  // porque la respuesta de /convertir no incluye las líneas mapeadas al shape de BolsaDetalle.
  async convertirBolsa(bolsaId: string): Promise<BolsaDetalle> {
    const res = await apiFetch(`${BASE}/api/cotizaciones/bolsas/${bolsaId}/convertir`, {
      method: 'POST',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      const msg = err?.error?.message ?? 'Error al crear el PDF';
      throw new Error(typeof msg === 'string' ? msg : 'Error al crear el PDF');
    }
    return cotizacionDetalleService.obtenerBolsa(bolsaId);
  },

  // SPEC-133 (RN-002, UC-003): consulta el PDF vigente ya generado (SPEC-126, sin cambios).
  async obtenerPdf(bolsaId: string): Promise<{ pdf_base64: string }> {
    const res = await apiFetch(`${BASE}/api/cotizaciones/bolsas/${bolsaId}/pdf`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      const msg = err?.error?.message ?? 'Error al obtener el PDF';
      throw new Error(typeof msg === 'string' ? msg : 'Error al obtener el PDF');
    }
    const json = await res.json();
    return { pdf_base64: json.data.pdf_base64 };
  },

  async obtenerTiposMarcacion(): Promise<TipoMarcacion[]> {
    const res = await apiFetch(`${BASE}/api/tipos-marcacion`);
    if (!res.ok) return [];
    return res.json();
  },

  async duplicarLinea(bolsaId: string, lineaId: string): Promise<LineaCotizacion> {
    const res = await apiFetch(
      `${BASE}/api/cotizaciones/bolsas/${bolsaId}/lineas/${lineaId}/duplicar`,
      { method: 'POST' },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Error al duplicar' }));
      throw new Error((err as { error?: string }).error ?? 'Error al duplicar la línea');
    }
    const json = await res.json();
    return mapLinea(json.data as RawLinea);
  },

  async eliminarLinea(bolsaId: string, lineaId: string): Promise<void> {
    const res = await apiFetch(
      `${BASE}/api/cotizaciones/bolsas/${bolsaId}/lineas/${lineaId}`,
      { method: 'DELETE' },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Error al eliminar' }));
      throw new Error((err as { error?: string }).error ?? 'Error al eliminar la línea');
    }
  },

  async agregarLinea(
    bolsaId: string,
    dto: { producto_id: string; cantidad: number; imagen_url?: string | null },
  ): Promise<LineaCotizacion> {
    const res = await apiFetch(
      `${BASE}/api/cotizaciones/bolsas/${bolsaId}/lineas`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      const msg = err?.error?.message ?? err?.error ?? 'Error al agregar el producto';
      throw new Error(typeof msg === 'string' ? msg : 'Error al agregar el producto');
    }
    const json = await res.json();
    return mapLinea(json.data.linea as RawLinea);
  },

  async guardarCotizacion(
    bolsaId: string,
    lineas: Array<{
      id: string;
      color_seleccionado: string | null;
      codigo_color_seleccionado: string | null;
      tipo_marcacion_id: number | null;
      precio_venta: number;
      cantidad: number;
      transporte: number;
      marcacion_opcion_id: number | null;
      marcacion_precio: number | null;
      marcacion_proveedor_id: number | null;
      // SPEC-206 (CA-035) — esquema de la marcación confirmada; null sin marcación.
      marcacion_esquema: EsquemaMarcacion | null;
      // SPEC-208 (RN-008) — solo en líneas con copia de opciones; nunca se envía un precio.
      opcion_precio_indice?: number;
    }>,
  ): Promise<{ convertida: boolean; codigoCotizacion: string | null; total?: number | null; lineas?: LineaGuardada[] }> {
    const res = await apiFetch(`${BASE}/api/cotizaciones/bolsas/${bolsaId}/guardar-cotizacion`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lineas }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Error al guardar' }));
      throw new Error(err.error ?? 'Error al guardar la cotización');
    }
    const body = await res.json();
    // SPEC-182 (UC-004) — guardar con marcación puede convertir la bolsa automáticamente.
    // SPEC-195 (CA-023) — total y líneas tal como quedaron persistidos; un backend anterior no
    // los envía (total null, sin líneas que adoptar).
    const lineasGuardadas: Array<{
      id: string;
      cantidad: number;
      precio_unitario?: number;
      descuento_proveedor: number;
      precio_con_descuento: number | null;
      origen_descuento?: OrigenDescuento | null;
      subtotal: number;
    }> = body.lineas ?? [];
    return {
      convertida: !!body.convertida,
      codigoCotizacion: body.codigo_cotizacion ?? null,
      total: body.total ?? null,
      lineas: lineasGuardadas.map((l) => ({
        id: l.id,
        cantidad: l.cantidad,
        // SPEC-208 — ausente si el backend no lo envía: el detalle conserva el de la línea.
        ...(l.precio_unitario !== undefined && { precioUnitario: l.precio_unitario }),
        descuentoProveedor: l.descuento_proveedor,
        precioConDescuento: l.precio_con_descuento,
        // SPEC-207 (RN-013) — ausente si el backend no lo envía: el detalle conserva el de la línea.
        ...(l.origen_descuento !== undefined && { origenDescuento: l.origen_descuento }),
        subtotal: l.subtotal,
      })),
    };
  },

  async obtenerConfigMarcacion(
    proveedorId: number,
    tipoMarcacionId: number,
  ): Promise<MarcacionConfigResponse | null> {
    const res = await apiFetch(
      `${BASE}/api/marcacion/config?proveedorId=${proveedorId}&tipoMarcacionId=${tipoMarcacionId}`,
    );
    if (!res.ok) return null;
    const json = await res.json();
    return json.data as MarcacionConfigResponse;
  },

  // SPEC-152 — productoProveedorId (proveedor del producto de la línea) condiciona la
  // aparición de "Catálogos Promocionales" en la lista. Opcional: solo se añade al
  // querystring cuando existe; el backend decide (fuente única de verdad).
  async obtenerProveedoresMarcacion(
    tipoMarcacionId: number,
    productoProveedorId: number | null = null,
  ): Promise<ProveedorMarcacion[]> {
    const qs = new URLSearchParams({ tipoMarcacionId: String(tipoMarcacionId) });
    if (productoProveedorId != null) qs.set('productoProveedorId', String(productoProveedorId));
    const res = await apiFetch(`${BASE}/api/marcacion/proveedores?${qs.toString()}`);
    if (!res.ok) return [];
    const json = await res.json();
    return (json.data as ProveedorMarcacion[]) ?? [];
  },
};
